import { Inject, inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { filter, map, Observable, take, Subject } from 'rxjs';
import { AutoMatchStatusType } from 'src/app/compliance/api/enums/auto-match-status-type.enum';
import { AutoMatchRequirementsDto } from 'src/app/compliance/api/models/auto-match-requirements-dto.model';
import { RequirementDto } from 'src/app/compliance/api/models/requirement-dto.model';
import { ComplianceReportDocumentType } from 'src/app/compliance/models/compliance-report-models';
import { updateComplianceNoteForRequirement } from 'src/app/compliance/store/actions/compliance-report-new.actions';
import { WINDOW } from 'src/app/core/core.module';
import { NoteDialogComponent } from 'src/app/shared/components/note-dialog/note-dialog.component';
import { RequirementDetailsViewerService } from 'src/app/shared/services/requirement-details-viewer.service';
import { AutoMatchRequirementDocumentInfo } from '../../../api/models/auto-match-requirement-document-info.model';
import { AutoMatchResultDto } from 'src/app/compliance/api/models/auto-match-result-dto.model';
import { ComplianceReportRequirementDto } from 'src/app/compliance/api/models/compliance-report-requirement-dto.model';
import { isNullOrUndefined } from 'src/app/shared/utilities/obj-utilities';
import { DocumentTypesClientWrapper } from 'src/app/compliance/document-types-client-wrapper';
import { Owner } from 'src/app/shared/helpers/owner.decorator';
import { DocumentForComplianceReportDto } from 'src/app/compliance/api/models/document-for-compliance-report-dto.model';
import { DocumentAdminClient } from 'src/app/compliance/api/document-admin-client.service';

@UntilDestroy()
@Owner('Mic Drop')
@Injectable({
    providedIn: 'root'
})
export class ComplianceReportHelperService {
    private readonly _viewerService: RequirementDetailsViewerService = inject(RequirementDetailsViewerService);
    private readonly _docAdminService: DocumentAdminClient = inject(DocumentAdminClient);
    private readonly _documentTypesService: DocumentTypesClientWrapper = inject(DocumentTypesClientWrapper);
    private readonly _dialog: MatDialog = inject(MatDialog);
    private readonly store: Store = inject(Store);
    private readonly _router: Router = inject(Router);

    textRequirementsWindowPosition = { top: 10, left: 10 };
    exampleFileWindowPosition = { top: 10, left: 10 };
    exampleFileWindow: Window = null;
    doSave$ = new Subject<'qualify' | 'save' | null>();

    constructor(@Inject(WINDOW) private readonly _window: Window) {}

    triggerSave(method: 'qualify' | 'save'): void {
        this.doSave$.next(method);
    }

    showTextRequirements(requirement: RequirementDto, contractId: number): void {
        const docType = this.convertDocumentTypeDto2ToDocumentType(requirement);

        this._viewerService.showTextRequirements(
            docType,
            this.textRequirementsWindowPosition,
            this.exampleFileWindow,
            contractId
        );
    }

    showExampleFile(document: RequirementDto): void {
        if (!document.documentId) {
            return;
        }

        const docType = this.convertDocumentTypeDto2ToDocumentType(document);

        this.getDocumentBlob(document.documentId)
            .pipe(take(1), untilDestroyed(this))
            .subscribe((blob) => {
                const viewerUrl = this.getWindowUrl(blob);
                this.updateExampleFileWindowPosition();
                this.openExampleFileWindow(viewerUrl, docType);
                this.setExampleFileWindowOnloadProperty();
                this.getTextReqirementsFromService(document, docType);
            });
    }

    showComplianceNote(req: RequirementDto, readonly: boolean, canEditNotes: boolean, contractId: number): void {
        if (canEditNotes && !readonly) {
            const note = req.complianceNote ? req.complianceNote : null,
                mode = note && note.length > 0 ? 'Edit' : 'Add',
                dialogRef = this._dialog.open(NoteDialogComponent, {
                    data: { title: `${mode} Note`, note: note, readonly: false },
                    panelClass: 'nova__dialog'
                });

            this.setAfterClosedDialogAction(dialogRef, req, contractId, mode as 'Add' | 'Edit');
        } else {
            const note = req.complianceNote ? req.complianceNote : null;
            const mode = 'View';
            this._dialog.open(NoteDialogComponent, {
                data: { title: `${mode} Note`, note, readonly: true },
                panelClass: 'nova__dialog'
            });
        }
    }

    showCredentialingNote(req: RequirementDto): void {
        const note = req.credentialingNote ? req.credentialingNote : null;
        const mode = 'View';
        this._dialog.open(NoteDialogComponent, {
            data: { title: `${mode} Note`, note, readonly: true },
            panelClass: 'nova__dialog'
        });
    }

    createRequirementSet(contractId: number, documentIds: number[]): void {
        this._router.navigate(['/compliance/contracts', contractId, 'doc-editing'], {
            queryParams: { 'requirement-ids': documentIds.join(',') }
        });
    }

    mapAutoMatchResult(items: AutoMatchRequirementDocumentInfo[]): AutoMatchRequirementDocumentInfo[] {
        let lastPassedIndex = items?.length || -1;

        return (
            items?.map((doc: AutoMatchRequirementDocumentInfo, index: number) => {
                lastPassedIndex =
                    doc.autoMatchStatus === AutoMatchStatusType.Passed && lastPassedIndex > index
                        ? index
                        : lastPassedIndex;

                return {
                    ...doc,
                    autoMatchStatusText: this.toStatusText(doc.autoMatchStatus),
                    isLastToReceivePassedStatus: lastPassedIndex === index
                };
            }) || []
        );
    }

    documentIsCheckedByDefault(
        requirement: RequirementDto,
        document: DocumentForComplianceReportDto,
        automatchData?: AutoMatchRequirementsDto
    ): boolean {
        if (!automatchData) {
            return document.isSelected;
        }

        if (!automatchData.documents[requirement.documentTypeId]) {
            return document.isSelected;
        }
        const automatchDataForDocument = automatchData?.documents[requirement.documentTypeId]?.find((doc) => {
            return doc.candidateDocumentId === document.candidateDocumentId;
        });
        if (!automatchDataForDocument) {
            return document.isSelected;
        }
        const documentIsPassedAndRequired =
            automatchDataForDocument.autoMatchStatus === AutoMatchStatusType.Passed && requirement.isRequired;
        const documentIsFailed = automatchDataForDocument.autoMatchStatus === AutoMatchStatusType.Failed;
        if (documentIsPassedAndRequired) {
            return true;
        }

        if (documentIsFailed) {
            return false;
        }

        return document.isSelected;
    }

    updateRequirementsWithModifiedRequirements(
        contractId: number,
        requirements: RequirementDto[],
        modifiedRequirements: RequirementDto[],
        automatchData: AutoMatchRequirementsDto
    ) {
        const returnValue = {
            contractId: contractId,
            requirements: requirements.map((req) => {
                if (modifiedRequirements.some((mr) => mr?.documentTypeId === req?.documentTypeId)) {
                    const modifiedRequirement = modifiedRequirements.find(
                        (mr) => mr?.documentTypeId === req?.documentTypeId
                    );
                    return modifiedRequirement;
                }

                return req;
            }),
            automatchData: automatchData,
            oldRequirements: requirements,
            modifiedRequirements: modifiedRequirements
        };
        return returnValue;
    }

    private fillQualificationAutomatchData(
        automatchData: AutoMatchRequirementsDto,
        req: RequirementDto
    ): AutoMatchResultDto[] {
        return !automatchData
            ? null
            : automatchData.documents[req.documentTypeId]
                  ?.map((doc) => {
                      const foundDoc = req.documents.find(
                          (reqDoc) => reqDoc.candidateDocumentId === doc.candidateDocumentId
                      );
                      return {
                          ...foundDoc,
                          autoMatchStatus: doc.autoMatchStatus,
                          autoMatchFields: doc.autoMatchFields
                      };
                  })
                  .filter((doc: DocumentForComplianceReportDto) => doc?.autoMatchStatus !== AutoMatchStatusType.None)
                  .map((doc) => {
                      const retVal = {
                          candidateDocumentId: doc?.candidateDocumentId,
                          autoMatchStatus: doc?.autoMatchStatus,
                          autoMatchFields: doc?.autoMatchFields,
                          autoMatchStatusText: '',
                          isLastToReceivePassedStatus: false
                      } as AutoMatchResultDto;
                      return retVal;
                  });
    }

    /**
     * Generates the request object to qualify contract's requirements
     * THIS IS TO BE USED AFTER THE USER HAS CLICKED 'QUALIFY'
     * @param request
     * @returns
     */
    getQualifiedRequirementsRequest(request: {
        contractId: number;
        originalRequirements: RequirementDto[];
        stateRequirements: RequirementDto[];
        modifiedRequirements: RequirementDto[];
        automatchData: AutoMatchRequirementsDto;
        selectionMap: { [key: string]: { [key: string]: boolean } };
    }): { contractId: number; requirements: ComplianceReportRequirementDto[] } {
        const getDocState = (req: RequirementDto) => {
            if (!req.documents || req.documents.length === 0) {
                return {
                    otherRequirementId: null,
                    candidateDocumentId: null,
                    selected: null
                };
            }

            const results = [];

            for (const doc of req.documents) {
                const requirementKey = doc.requirementId?.toString();
                const candidateKey = doc.candidateDocumentId?.toString();
                const selectionByRequirement = requirementKey ? request.selectionMap?.[requirementKey] : undefined;
                const selectionValue = candidateKey ? selectionByRequirement?.[candidateKey] : undefined;

                if (!isNullOrUndefined(selectionValue)) {
                    results.push({
                        otherRequirementId: doc.requirementId,
                        candidateDocumentId: doc.candidateDocumentId || null,
                        selected: selectionValue
                    });
                }
            }

            if (results.length > 0) {
                return results.some((r) => r.selected) ? { ...results.find((r) => r.selected) } : { ...results[0] };
            }

            return {
                otherRequirementId: req.documents[0]?.requirementId,
                candidateDocumentId: req.documents[0]?.candidateDocumentId || null,
                selected: req.documents[0]?.isSelected
            };
        };

        return {
            contractId: request.contractId,
            requirements: request.stateRequirements.map((req) => {
                const documentStateResult = getDocState(req);
                const modified = request.modifiedRequirements.find((mr) => mr.documentTypeId === req.documentTypeId);
                const originalReq = request.originalRequirements.find((or) => or.documentTypeId === req.documentTypeId);
                const isRequiredOverridden =
                    modified && originalReq ? originalReq.isRequired !== modified.isRequired : false;

                const result = {
                    ...documentStateResult,
                    documentTypeId: req.documentTypeId,
                    requiresManualReview: req.requiresManualReview,
                    overrideLevel: req.overrideLevel,
                    isRequiredByClient: null,
                    requiredOverridden: isRequiredOverridden,
                    required: modified?.isRequired ?? req.isRequired,
                    autoMatchResults: this.fillQualificationAutomatchData(request.automatchData, req)
                } as ComplianceReportRequirementDto;

                // Use for debug purposes
                console.log(
                    `[${result.required ? 'X' : ' '}] ${req.documentTypeName} - [${result.selected ? 'X' : ' '}] ${req.documents?.find((doc) => doc.candidateDocumentId === result.candidateDocumentId)?.fileName} - requirementId: ${result.otherRequirementId}`
                );

                return result;
            })
        };
    }

    getQualifyRequest(request: {
        contractId: number;
        originalRequirements: RequirementDto[];
        stateRequirements: RequirementDto[];
        modifiedRequirements: RequirementDto[];
        defaultCheckedDocuments: DocumentForComplianceReportDto[];
        failedAutomatchDocuments: number[];
        automatchData: AutoMatchRequirementsDto;
    }): { contractId: number; requirements: ComplianceReportRequirementDto[] } {
        return {
            contractId: request.contractId,
            requirements: request.stateRequirements.map((req) => {
                const autoSelectedDocument = req.documents.find((doc) => doc.isSelected === true);

                const defaultCheckedDocument = req.documents.find((doc) =>
                    request.defaultCheckedDocuments.some(
                        (checkedDoc) =>
                            checkedDoc.candidateDocumentId === doc.candidateDocumentId &&
                            checkedDoc.requirementId === doc.requirementId
                    )
                );

                const candidateDocumentId =
                    defaultCheckedDocument?.candidateDocumentId || req.documents[0]?.candidateDocumentId || null;
                const isFailedAutomatch =
                    request.failedAutomatchDocuments.length > 0
                        ? request.failedAutomatchDocuments.includes(candidateDocumentId)
                        : undefined;
                let isSelected = !!defaultCheckedDocument ? true : req.documents[0]?.isSelected;
                isSelected = isFailedAutomatch === true ? false : isSelected;

                let result = {
                    otherRequirementId: defaultCheckedDocument?.requirementId ?? req.documents[0]?.requirementId,
                    documentTypeId: req.documentTypeId,
                    requiresManualReview: req.requiresManualReview,
                    overrideLevel: req.overrideLevel,
                    isRequiredByClient: null,
                    requiredOverridden: false,
                    candidateDocumentId: candidateDocumentId,
                    required: req.isRequired === true ? true : !!defaultCheckedDocument,
                    selected: isSelected,
                    autoMatchResults: this.fillQualificationAutomatchData(request.automatchData, req)
                } as ComplianceReportRequirementDto;

                const modified = request.modifiedRequirements.find((mr) => mr.documentTypeId === req.documentTypeId);

                if (modified) {
                    const checkedDoc = modified.documents.find((doc) => doc.isSelected === true);

                    result = {
                        ...result,
                        otherRequirementId: checkedDoc?.requirementId ?? result.otherRequirementId,
                        candidateDocumentId: checkedDoc?.candidateDocumentId ?? result.candidateDocumentId,
                        required: modified.isRequired,
                        selected: checkedDoc?.isSelected === true ? true : false,
                        requiredOverridden: request.originalRequirements.some(
                            (or) => or.documentTypeId === req.documentTypeId && or.isRequired !== modified.isRequired
                        )
                    };

                    // This means that a document was auto-selected (new upload),
                    // but the user unselected a different document (passed automatch) manually
                    if (!result.selected && autoSelectedDocument?.candidateDocumentId !== result.candidateDocumentId) {
                        result = {
                            ...result,
                            otherRequirementId: autoSelectedDocument?.requirementId ?? result.otherRequirementId,
                            candidateDocumentId: autoSelectedDocument?.candidateDocumentId ?? result.candidateDocumentId
                        };
                    }
                }

                // Use for debug purposes
                // console.log(
                //     `[${result.required ? 'X' : ' '}] ${req.documentTypeName} - [${result.selected ? 'X' : ' '}] ${req.documents?.find((doc) => doc.candidateDocumentId === result.candidateDocumentId)?.fileName}`
                // );

                return result;
            })
        };
    }

    getComplianceReportRequirementDtoFromQualifyRequirementsActionParameters(
        contractId: number,
        requirements: RequirementDto[],
        oldRequirements: RequirementDto[],
        modifiedRequirements: RequirementDto[],
        automatchData: AutoMatchRequirementsDto
    ) {
        return {
            contractId: contractId,
            requirements: requirements.map((req) => {
                const modifiedRequirement = modifiedRequirements.find(
                    (mr) => mr?.documentTypeId === req?.documentTypeId
                );

                let selectedDoc;

                if (modifiedRequirement) {
                    selectedDoc = modifiedRequirement?.documents?.find((doc) => doc.isSelected === true);
                } else {
                    selectedDoc = req?.documents?.find((doc) => doc.isSelected === true);
                }

                const candidateDocId = selectedDoc?.candidateDocumentId;
                const defaultCandidateDocumentId = req?.documents[0]?.candidateDocumentId;
                const hasSelectedDocument = !!candidateDocId;
                const oldRequirement = oldRequirements.find((or) => or?.documentTypeId === req?.documentTypeId);
                const requiredOverridden =
                    modifiedRequirement && oldRequirement
                        ? modifiedRequirement.isRequired !== oldRequirement.isRequired
                        : false;

                const returnValue = {
                    otherRequirementId: selectedDoc?.requirementId,
                    documentTypeId: req.documentTypeId,
                    candidateDocumentId: candidateDocId || defaultCandidateDocumentId,
                    selected: !!candidateDocId ? true : null,
                    required: req.isRequired === true ? true : hasSelectedDocument,
                    requiresManualReview: req.requiresManualReview,
                    overrideLevel: req.overrideLevel,
                    isRequiredByClient: null,
                    requiredOverridden: requiredOverridden,
                    autoMatchResults: !automatchData
                        ? null
                        : automatchData.documents[req.documentTypeId]
                              ?.map((doc) => {
                                  const foundDoc = req.documents.find(
                                      (reqDoc) => reqDoc.candidateDocumentId === doc.candidateDocumentId
                                  );
                                  return {
                                      ...foundDoc,
                                      autoMatchStatus: doc.autoMatchStatus,
                                      autoMatchFields: doc.autoMatchFields
                                  };
                              })
                              .filter(
                                  (doc: DocumentForComplianceReportDto) =>
                                      doc?.autoMatchStatus !== AutoMatchStatusType.None
                              )
                              .map((doc) => {
                                  const retVal = {
                                      candidateDocumentId: doc?.candidateDocumentId,
                                      autoMatchStatus: doc?.autoMatchStatus,
                                      autoMatchFields: doc?.autoMatchFields,
                                      autoMatchStatusText: '',
                                      isLastToReceivePassedStatus: false
                                  } as AutoMatchResultDto;
                                  return retVal;
                              })
                } as ComplianceReportRequirementDto;

                return returnValue;
            })
        };
    }

    getIsRequiredValue(oldReqs: RequirementDto[], newReq: RequirementDto): boolean {
        const currentRequirementIsRequired = newReq.isRequired;
        const requirementIsBeingUnrequired = this.isRequirementBeingUnrequired(oldReqs, newReq);
        const newDocumentIsBeingSelected = this.isNewDocumentBeingSelected(oldReqs, newReq);

        if (newDocumentIsBeingSelected) {
            return true;
        }

        if (requirementIsBeingUnrequired) {
            return false;
        }

        return currentRequirementIsRequired;
    }

    getRequirementIdFromSelectedDocument(oldReqs: RequirementDto[], newReq: RequirementDto): number {
        const newCandidateRequirementId = this.getRequirementId(oldReqs, newReq);
        const oldCandidateRequirementID = oldReqs
            .find((oldReq) => oldReq.documentTypeId === newReq.documentTypeId)
            ?.documents?.find((doc) => doc.isSelected === true)?.requirementId;
        const requirementIsBeingUnrequired = this.isRequirementBeingUnrequired(oldReqs, newReq);
        const newDocumentIsBeingSelected = this.isNewDocumentBeingSelected(oldReqs, newReq);

        if (newDocumentIsBeingSelected) {
            return newCandidateRequirementId;
        }

        if (requirementIsBeingUnrequired) {
            if (!newDocumentIsBeingSelected) {
                return oldCandidateRequirementID;
            }
            return newCandidateRequirementId;
        }

        if (!newDocumentIsBeingSelected) {
            return oldCandidateRequirementID;
        }

        return newCandidateRequirementId;
    }

    getSelectedCandidateDocumentId(oldReqs: RequirementDto[], newReq: RequirementDto): number {
        const newCandidateDocumentId = this.getCandidateDocumentId(oldReqs, newReq);
        const oldCandidateDocumentID = oldReqs
            .find((oldReq) => oldReq.documentTypeId === newReq.documentTypeId)
            ?.documents?.find((doc) => doc.isSelected === true)?.candidateDocumentId;
        const requirementIsBeingUnrequired = this.isRequirementBeingUnrequired(oldReqs, newReq);
        const newDocumentIsBeingSelected = this.isNewDocumentBeingSelected(oldReqs, newReq);

        if (newDocumentIsBeingSelected) {
            return newCandidateDocumentId;
        }

        if (requirementIsBeingUnrequired) {
            if (!newDocumentIsBeingSelected) {
                return oldCandidateDocumentID;
            }
            return newCandidateDocumentId;
        }

        if (!newDocumentIsBeingSelected) {
            return oldCandidateDocumentID;
        }

        return newCandidateDocumentId;
    }

    isNewDocumentBeingSelected(oldReqs: RequirementDto[], newReq: RequirementDto): boolean {
        const selectedDoc = newReq.documents?.find((doc) => doc.isSelected === true);
        const newlySelectedCandidateDocumentId = selectedDoc?.candidateDocumentId;
        const previouslySelectedCandidateDocumentId = oldReqs
            ?.find((oldReq) => oldReq.documentTypeId === newReq.documentTypeId)
            ?.documents?.find((doc) => doc.isSelected === true)?.candidateDocumentId;
        const selectedDocumentIdHasChanged = previouslySelectedCandidateDocumentId !== newlySelectedCandidateDocumentId;
        const newDocumentIsBeingSelected = selectedDocumentIdHasChanged && !!newlySelectedCandidateDocumentId;
        return newDocumentIsBeingSelected;
    }

    mergeCheckedDocumentsIntoModifiedRequirements(
        requirements: RequirementDto[],
        checkedDocuments: DocumentForComplianceReportDto[]
    ): RequirementDto[] {
        const newReqs = requirements.map((req) => {
            const newReq = { ...req };
            newReq.documents = newReq.documents.map((doc) => {
                const newDoc = { ...doc };
                const isChecked = checkedDocuments.some((checkedDoc) => {
                    return (
                        checkedDoc.candidateDocumentId === doc.candidateDocumentId &&
                        checkedDoc.requirementId === doc.requirementId
                    );
                });
                newDoc.isSelected = isChecked;
                return newDoc;
            });
            return newReq;
        });
        return newReqs;
    }

    mergeFailedDocumentsIntoModifiedRequirements(
        requirements: RequirementDto[],
        failedCandidateDocumentIds: number[],
        manuallySelectedDocuments: { document: DocumentForComplianceReportDto; documentTypeId: number }[]
    ): RequirementDto[] {
        const newReqs = requirements.map((req) => {
            const newReq = { ...req };
            newReq.documents = newReq.documents.map((doc) => {
                const newDoc = { ...doc };

                const documentIsPresentInFailedDocs = failedCandidateDocumentIds.some((failedId) => {
                    return failedId === doc.candidateDocumentId;
                });

                const documentIsManuallySelected = manuallySelectedDocuments.some((manuallySelectedDoc) => {
                    return (
                        manuallySelectedDoc.documentTypeId === req.documentTypeId &&
                        manuallySelectedDoc.document.candidateDocumentId === doc.candidateDocumentId &&
                        manuallySelectedDoc.document.requirementId === doc.requirementId
                    );
                });

                if (documentIsPresentInFailedDocs && !documentIsManuallySelected) {
                    newDoc.isSelected = false;
                }

                return newDoc;
            });
            return newReq;
        });
        return newReqs;
    }

    private getRequirementId(oldReqs: RequirementDto[], newReq: RequirementDto): number {
        const selectedDoc = newReq.documents?.find((doc) => doc.isSelected === true);
        const newlySelectedRequirementId = selectedDoc?.requirementId;
        const previouslySelectedRequirementId = oldReqs
            ?.find((oldReq) => oldReq.documentTypeId === newReq.documentTypeId)
            ?.documents?.find((doc) => doc.isSelected === true)?.requirementId;
        const requirementId = newlySelectedRequirementId || previouslySelectedRequirementId;
        return requirementId;
    }

    private getCandidateDocumentId(oldReqs: RequirementDto[], newReq: RequirementDto): number {
        const selectedDoc = newReq.documents?.find((doc) => doc.isSelected === true);
        const newlySelectedCandidateDocumentId = selectedDoc?.candidateDocumentId;
        const previouslySelectedCandidateDocumentId = oldReqs
            ?.find((oldReq) => oldReq.documentTypeId === newReq.documentTypeId)
            ?.documents?.find((doc) => doc.isSelected === true)?.candidateDocumentId;
        const candidateDocumentId = newlySelectedCandidateDocumentId || previouslySelectedCandidateDocumentId;
        return candidateDocumentId;
    }

    private isRequirementBeingUnrequired(oldReqs: RequirementDto[], newReq: RequirementDto): boolean {
        const previousRequirement = oldReqs?.find((oldReq) => oldReq.documentTypeId === newReq.documentTypeId);
        const previousRequirementWasRequired = previousRequirement?.isRequired;
        const currentRequirementIsRequired = newReq.isRequired;
        const requirementIsBeingUnrequired = previousRequirementWasRequired && !currentRequirementIsRequired;
        return requirementIsBeingUnrequired;
    }

    private getDocumentBlob(documentId: number): Observable<Blob> {
        return this._docAdminService.downloadFile(documentId).pipe(map((x) => x?.data));
    }

    private updateExampleFileWindowPosition(): void {
        if (this.exampleFileWindow) {
            if (this.exampleFileWindow.screenTop) {
                this.exampleFileWindowPosition.top = this.exampleFileWindow.screenTop;
            }

            if (this.exampleFileWindow.screenLeft) {
                this.exampleFileWindowPosition.left = this.exampleFileWindow.screenLeft;
            }

            this.exampleFileWindow.close();
        }
    }

    private getWindowUrl(blob: Blob): string {
        return window.URL.createObjectURL(blob);
    }

    private openExampleFileWindow(viewerUrl: string, docType: ComplianceReportDocumentType): void {
        this.exampleFileWindow = this._window.open(
            viewerUrl,
            docType.exampleFile,
            `height=${this._window.outerHeight * 0.8},width=${this._window.outerWidth * 0.5},top=${
                this.exampleFileWindowPosition.top
            },left=${this.exampleFileWindowPosition.left}`
        );
    }

    private setExampleFileWindowOnloadProperty(): void {
        this.exampleFileWindow.onload = () => {
            this.exampleFileWindow.onbeforeunload = ($event) => {
                const window: Window = $event.currentTarget as Window;
                if (window) {
                    this.exampleFileWindowPosition.top = window.screenTop;
                    this.exampleFileWindowPosition.left = window.screenLeft;
                }
            };
        };
    }

    private getTextReqirementsFromService(document: RequirementDto, docType: ComplianceReportDocumentType): void {
        (this.exampleFileWindow as any).data = {
            reqs: this._documentTypesService.getDocumentTypeTextRequirements(
                document.documentTypeId,
                docType.facilityId,
                docType.associationId
            )
        };
    }

    private convertDocumentTypeDto2ToDocumentType(document: RequirementDto): ComplianceReportDocumentType {
        return {
            id: document.documentTypeId,
            name: document.documentTypeName,
            categoryId: document.documentTypeCategoryId,
            category: document.documentTypeCategoryName,
            isSignedContract: document.isSignedContract,
            requiresExpiration: document.requiresExpiration,
            stateLicensure: document.isStateLicensure,
            exampleFile: document.exampleFile,
            documentSuffix: document.documentSuffix,
            hasRequirements: document.documents.length > 0,
            hasNonGlobalDescription: document.hasNonGlobalDescription,
            hasGlobalOnlyOrExternalDescription: document.hasGlobalOnlyOrExternalDescription,
            documentId: document.documentId,
            codeSets: null,
            codes: null,
            liaisonUserName: '',
            requiresManualReview: document.requiresManualReview,
            facilityId: document.facilityId,
            associationId: document.qmAssociationId,
            expirationDateDisabled: document.expirationDateDisabled,
            isRequiredForUnit: document.isRequiredForUnit,
            isPreferredForUnit: document.isPreferredForUnit,
            isRequiredAndCurrent: document.isRequiredAndCurrent,
            isDisabledCategory: false
        } as ComplianceReportDocumentType;
    }

    private updateComplianceNoteForRequirement(
        req: RequirementDto,
        contractId: number,
        result: string,
        mode: 'Add' | 'Edit'
    ): void {
        this.store.dispatch(
            updateComplianceNoteForRequirement({
                contractId: contractId,
                documentTypeId: req.documentTypeId,
                requirementId: req.requirementId,
                complianceNote: result,
                addOrEdit: mode,
                isRequired: req.isRequired
            })
        );
    }

    private setAfterClosedDialogAction(
        dialogRef: MatDialogRef<NoteDialogComponent, any>,
        req: RequirementDto,
        contractId: number,
        mode: 'Add' | 'Edit'
    ): void {
        dialogRef
            .afterClosed()
            .pipe(
                filter((result) => result || mode === 'Edit'),
                take(1),
                untilDestroyed(this)
            )
            .subscribe((result) => {
                if (!isNullOrUndefined(result)) {
                    this.updateComplianceNoteForRequirement(req, contractId, result, mode as 'Add' | 'Edit');
                }
            });
    }

    private toStatusText(enumValue?: AutoMatchStatusType) {
        switch (enumValue) {
            case AutoMatchStatusType.Passed:
                return 'Pass';
            case AutoMatchStatusType.Failed:
                return 'Fail';
            case AutoMatchStatusType.ReviewNeeded:
                return 'Review Needed';
            case AutoMatchStatusType.Error:
                return 'Error';
            default:
                return '';
        }
    }
}
